/**
 * Moving a priority definition from one priority box to another (PREC-3908).
 *
 * Before this, re-prioritising a definition meant retyping it in the target box and deleting the
 * original — the workaround the ticket describes. The move itself is only a `priorityLevel`
 * change, so it lives here as a pure function over the flat `priorities` array both the general
 * signal settings and the tracker list settings keep their definitions in.
 */

/** The minimum a priority definition must expose to be identified and moved. */
export interface MovablePriorityItem<TLevel extends string> {
	id?: number;
	title: string;
	priorityLevel: TLevel;
	order?: number;
}

/**
 * A drag id that survives a list with unsaved items.
 *
 * A definition created in this session has an `id` (a `Date.now()` stand-in) but one loaded from
 * the API may collide with it, so the level+title pair — the same identity the existing edit and
 * delete handlers match on — is the fallback and the tiebreaker.
 */
export const priorityItemKey = <TLevel extends string>(item: MovablePriorityItem<TLevel>): string =>
	`${item.priorityLevel}::${item.title}`;

/**
 * The array with the identified item reassigned to `toLevel`, appended after the items already
 * there.
 *
 * Returns the original array reference when the move is a no-op — the item is unknown, or it is
 * already in the target box — so a drag that ends where it started cannot mark the form dirty.
 */
export const movePriorityItem = <TItem extends MovablePriorityItem<string>>(
	items: TItem[],
	itemKey: string,
	toLevel: TItem["priorityLevel"]
): TItem[] => {
	const index = items.findIndex((item) => priorityItemKey(item) === itemKey);

	if (index === -1 || items[index].priorityLevel === toLevel) {
		return items;
	}

	const moved = { ...items[index], priorityLevel: toLevel };
	const remaining = items.filter((_, i) => i !== index);

	// Appended rather than spliced in place: the target box's own order is meaningful to the user
	// who arranged it, and a drop carries no position within it.
	const lastInTarget = remaining.reduce(
		(last, item, i) => (item.priorityLevel === toLevel ? i : last),
		-1
	);

	return [...remaining.slice(0, lastInTarget + 1), moved, ...remaining.slice(lastInTarget + 1)];
};
